import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';

const VideoPlayer = ({
  src,
  playerRef,
  controls = true,
  muted = true,
  autoPlay = false,
  loop = false,
  playsInline = true,
  containerClassName = 'w-full h-full',
  videoClassName = 'w-full h-auto object-contain',
  videoStyle,
  initialSeek = 0,
  onReady,
  onCueChange
}) => {
  const onReadyRef = useRef(onReady);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    const handleTextTrackChange = () => {
      if (!videoRef.current) {
        return;
      }
      const showingTrack = [...videoRef.current.textTracks].find(track => track.mode === "showing");
      if (showingTrack) {
        showingTrack.oncuechange = () => {
          onCueChange(showingTrack);
        }
      }
    }

    videoRef.current.textTracks.addEventListener("change", handleTextTrackChange);

    return () => {
      videoRef.current?.textTracks?.removeEventListener("change", handleTextTrackChange);
    }
  }, [videoRef.current]);

  useEffect(() => {
    if (Hls.isSupported() && videoRef.current) {
      // Destroy existing HLS instance if it exists
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      const hls = new Hls({
        xhrSetup: (xhr, url) => {
          // Preserve the original URL with query parameters
          xhr.open('GET', url, true);
        },
        debug: false
      });
      hlsRef.current = hls;

      console.log('>>> src:', src);
      hls.loadSource(src);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('>>> MANIFEST_PARSED');
        if (onReadyRef.current) {
          onReadyRef.current(videoRef.current);
          onReadyRef.current = null;
        }
        if (initialSeek) {
          videoRef.current.currentTime = initialSeek;
        }
        if (playerRef) {
          playerRef.current = videoRef.current;
        }
      });

      return () => {
        if (hlsRef.current) {
          try {
            hlsRef.current.destroy();
          } catch (error) {
            // Silently handle cleanup errors
            console.debug('HLS cleanup error (expected during navigation):', error);
          }
          hlsRef.current = null;
        }
      };
    } else if (videoRef.current) {
      // Fallback for browsers that don't support HLS.js
      videoRef.current.src = src;
      if (playerRef) {
        playerRef.current = videoRef.current;
      }
    }
  }, [src, playerRef]);

  const handleClick = () => {
    if (videoRef.current) {
      console.log('>>> handleClick');
      videoRef.current.play();
    }
  }

  return (
    <div className={containerClassName} onClick={handleClick}>
      <video
        ref={videoRef}
        controls={controls}
        muted={muted}
        autoPlay={autoPlay}
        loop={loop}
        playsInline={playsInline}
        className={videoClassName}
        style={{ maxWidth: '100%', ...(videoStyle || {}) }}
      />
    </div>
  );
};

export default VideoPlayer; 